import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { StatusChip } from "@/components/StatusChip";
import { Crosshair } from "@/components/Crosshair";
import {
  ArrowLeft, CreditCard, ExternalLink, Loader2, AlertTriangle, ShieldCheck, Banknote, Clock, Zap,
} from "lucide-react";
import { toast } from "@/lib/toast";

const CEIL = "#DAD6CE";
const money = (cents) =>
  "$" + ((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// payout_status (backend NONE/STARTED/PENDING/RESTRICTED/ENABLED/DISABLED/UNKNOWN) -> chip token.
// Prefer the backend's LIVE-verified chip token; fall back to the legacy status mapping.
function payoutChip(status) {
  if (status?.payout_chip) return status.payout_chip;
  if (status?.payout_ready_live === "UNKNOWN") return "unknown";
  const ps = (status?.payout_status || "").toUpperCase();
  if (ps === "ENABLED" || status?.payout_enabled) return "enabled";
  if (ps === "RESTRICTED") return "restricted";
  if (ps === "PENDING" || ps === "STARTED") return "in_progress";
  return "not_started"; // NONE / DISABLED / no account
}

// Human-readable copy for Stripe's disabled_reason machine codes.
const DISABLED_COPY = {
  "requirements.past_due": "Stripe needs more information before payouts can turn on.",
  "requirements.pending_verification": "Stripe is verifying the details you submitted.",
  "listed": "This account appears on a prohibited-business list and is under review.",
  "platform_paused": "Payouts were paused by the marketplace.",
  "rejected.fraud": "Stripe flagged this account for fraud risk.",
  "rejected.listed": "This account was rejected (prohibited business).",
  "rejected.other": "Stripe rejected this account.",
  "under_review": "Stripe is reviewing this account.",
};

function REQ_LABEL(code) {
  return (code || "")
    .replace(/_/g, " ")
    .replace(/\./g, " · ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function SellerPayouts() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pendingActivation, setPendingActivation] = useState(false);
  const [instant, setInstant] = useState(null);
  const [instantLoading, setInstantLoading] = useState(false);
  const [confirmCashout, setConfirmCashout] = useState(false);
  const [cashingOut, setCashingOut] = useState(false);

  const loadStatus = () =>
    api.get("/dealer/stripe/status").then(({ data }) => setStatus(data)).catch(() => {}).finally(() => setLoading(false));

  // Return-from-Stripe: refresh live status, then strip the ?stripe flag from the URL.
  // Runs only once auth resolves; a no-session return shows the logged-out card instead
  // (the account.updated webhook already synced payout status server-side).
  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    const flag = new URLSearchParams(location.search).get("stripe");
    if (flag === "return" || flag === "refresh") {
      setLoading(true);
      api.post("/dealer/stripe/refresh")
        .then(({ data }) => {
          setStatus(data);
          if (data.onboarding_status === "complete" || data.payout_status === "ENABLED")
            toast.success("Connected and payout-ready. Any listings waiting on payout setup are now live.", { label: "Payouts enabled" });
          else
            toast.info("Onboarding saved. Stripe may still need a few more details.");
        })
        .catch(() => loadStatus())
        .finally(() => {
          setLoading(false);
          navigate("/settings/payouts", { replace: true });
        });
    } else {
      loadStatus();
    }
  }, [authLoading, user]); // eslint-disable-line

  const startOnboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/dealer/stripe/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "connect_not_enabled") {
        setPendingActivation(true);
        toast.info("Marketplace payouts are being finalized — check back soon.");
      } else {
        toast.error(formatError(detail));
      }
      setBusy(false);
    }
  };

  const openDashboard = async () => {
    // Open the tab SYNCHRONOUSLY inside the click gesture so mobile popup blockers don't kill it,
    // then redirect it once the login link resolves. (Awaiting the fetch before window.open makes
    // the open non-user-initiated, which mobile Safari/Chrome silently block.)
    const win = window.open("", "_blank");
    if (win) win.opener = null;
    try {
      const { data } = await api.get("/dealer/stripe/login-link");
      if (win) {
        win.location.href = data.url;
      } else {
        window.location.href = data.url; // popup blocked entirely — fall back to this tab
      }
    } catch (err) {
      if (win) win.close();
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const chip = status ? payoutChip(status) : "not_started";
  const enabled = chip === "enabled";
  const restricted = chip === "restricted";
  const unknown = status?.payout_ready_live === "UNKNOWN" || chip === "unknown";
  const reconnect = !!status?.reconnect_required;
  const connected = !!status?.connected;
  const reqs = status?.requirements_due || [];
  const disabledReason = status?.disabled_reason;
  const awaiting = status?.sales_awaiting_payout || 0;
  const awaitingAmt = status?.amount_awaiting_cents || 0;

  // INSTANT PAYOUTS — opt-in cash-out to a debit card. Only queried once the account is payout-ready.
  const loadInstant = () => {
    setInstantLoading(true);
    api.get("/dealer/stripe/instant")
      .then(({ data }) => setInstant(data))
      .catch(() => setInstant(null))
      .finally(() => setInstantLoading(false));
  };

  const doInstantCashout = async () => {
    setCashingOut(true);
    try {
      const { data } = await api.post("/dealer/stripe/instant-payout");
      toast.success(
        `${money(data.amount_cents)} is on its way — instant payouts typically arrive within 30 minutes.`,
        { label: "Cashing out" });
      setConfirmCashout(false);
      loadInstant();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setCashingOut(false);
    }
  };

  useEffect(() => {
    if (!user || !enabled) return;
    loadInstant();
  }, [enabled, user]); // eslint-disable-line

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="seller-payouts-page">
        <Link to="/dashboard" data-testid="payouts-back-link"
          className="inline-flex items-center gap-2 text-[#8B8D90] hover:text-[#DAD6CE] font-mono uppercase text-[11px] tracking-wide transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
        </Link>

        <div className="flex items-center gap-3 mt-5">
          <Banknote className="w-6 h-6 text-olive-bright" />
          <h1 className="font-heading text-3xl uppercase tracking-tight" style={{ color: CEIL }}>Seller Payouts</h1>
        </div>
        <p className="text-sm font-body text-[#8B8D90] mt-2 max-w-[60ch]">
          Payments from your sales are held by OpticSwap until the buyer confirms the gear arrived, then paid out
          to your connected Stripe account. This is where you set that up and track its status.
        </p>
        <Crosshair className="my-6" />

        {authLoading || loading ? (
          <div className="flex items-center gap-2 text-[#8B8D90] font-mono text-sm py-10" data-testid="payouts-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading payout status…
          </div>
        ) : !user ? (
          <div className="border border-[#2E2F31] bg-[#111213] p-6" data-testid="payouts-loggedout">
            <div className="flex items-center gap-2 text-[#9BB37A] font-mono uppercase text-[11px] tracking-wide">
              <ShieldCheck className="w-4 h-4" /> Onboarding saved
            </div>
            <p className="text-[13px] font-body text-[#B7BABE] mt-3 max-w-[54ch]">
              Your payout setup with Stripe has been saved. Log in to OpticSwap to view and manage your
              payout status — you don't need to redo anything.
            </p>
            <Link to="/join" data-testid="payouts-login-link"
              className="inline-flex items-center gap-2 mt-4 border border-olive bg-olive hover:bg-olive-bright text-white font-mono uppercase text-[11px] tracking-wide px-4 py-2.5 transition-colors">
              Log in to continue
            </Link>
          </div>
        ) : (
          <div className="border border-[#2E2F31] bg-[#111213] p-6" data-testid="payouts-status-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#76797D] mb-2">Payout Status</div>
                <StatusChip state={chip} testId={`payout-status-chip-${chip}`} />
              </div>
              <div className="shrink-0">
                {enabled ? (
                  <button onClick={openDashboard} data-testid="payouts-dashboard-btn"
                    className="inline-flex items-center gap-2 border border-[#2E2F31] hover:border-[#3E3F42] text-[#DAD6CE] font-mono uppercase text-[11px] tracking-wide px-4 py-2.5 transition-colors">
                    <ExternalLink className="w-3.5 h-3.5" /> Stripe Dashboard
                  </button>
                ) : (
                  <button onClick={startOnboard} disabled={busy} data-testid="payouts-onboard-btn"
                    className="inline-flex items-center gap-2 border border-olive bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-mono uppercase text-[11px] tracking-wide px-4 py-2.5 transition-colors">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
                    {reconnect ? "Reconnect payouts" : connected ? "Finish setup" : "Set up payouts"}
                  </button>
                )}
              </div>
            </div>

            {enabled && (
              <p className="text-[12px] font-body text-[#9BB37A] mt-4 flex items-center gap-2" data-testid="payouts-enabled-copy">
                <ShieldCheck className="w-4 h-4" /> Connected and payout-ready. Released funds land in this account.
              </p>
            )}

            {/* INSTANT PAYOUTS — opt-in cash-out to a debit card in ~30 min (incl. weekends). The 1%
                Stripe fee is passed to the seller; standard automatic payouts stay free. */}
            {enabled && (
              <div className="mt-5 border border-[#2E2F31] bg-[#0d0e0f] p-5" data-testid="instant-payout-card">
                <div className="flex items-center gap-2 text-[#E8C46A] font-mono uppercase text-[11px] tracking-wide">
                  <Zap className="w-4 h-4" /> Instant payout
                </div>
                {instantLoading ? (
                  <div className="flex items-center gap-2 text-[#8B8D90] font-mono text-[12px] mt-3" data-testid="instant-payout-loading">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking instant availability…
                  </div>
                ) : !instant || !instant.available ? (
                  <p className="text-[12.5px] font-body text-[#8B8D90] mt-3 max-w-[56ch]" data-testid="instant-unavailable">
                    Instant payouts aren't available right now. Your standard payouts continue on the automatic schedule — free, as always.
                  </p>
                ) : instant.status === "no_card" ? (
                  <div className="mt-3" data-testid="instant-no-card">
                    <p className="text-[12.5px] font-body text-[#B7BABE] max-w-[58ch]">
                      Cash out available funds to a debit card in about 30 minutes, any day of the week. Add an
                      eligible debit card in your Stripe dashboard to turn this on. A 1% Stripe fee applies to
                      instant payouts only — standard payouts stay free.
                    </p>
                    <button onClick={openDashboard} data-testid="instant-add-card-btn"
                      className="inline-flex items-center gap-2 mt-3 border border-olive bg-olive hover:bg-olive-bright text-white font-mono uppercase text-[11px] tracking-wide px-4 py-2.5 transition-colors">
                      <CreditCard className="w-3.5 h-3.5" /> Add a debit card
                    </button>
                  </div>
                ) : (instant.status === "no_funds" || (instant.net_available_cents || 0) <= 0) ? (
                  <p className="text-[12.5px] font-body text-[#8B8D90] mt-3 max-w-[58ch]" data-testid="instant-no-funds">
                    No funds are available for an instant payout right now. New sales become available to cash out
                    once their payment is released.
                  </p>
                ) : (
                  <div className="mt-3" data-testid="instant-eligible">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#76797D]">Available to cash out</div>
                        <div className="font-heading text-2xl" style={{ color: CEIL }} data-testid="instant-available-amount">
                          {money(instant.net_available_cents)}
                        </div>
                      </div>
                      {instant.card && (
                        <div className="text-[11px] font-mono text-[#8B8D90]" data-testid="instant-card-label">
                          to {instant.card.brand || "debit"} •••• {instant.card.last4}
                        </div>
                      )}
                    </div>
                    <div className="mt-3 text-[12px] font-body text-[#8B8D90] space-y-1 max-w-[38ch]">
                      <div className="flex justify-between">
                        <span>Instant fee (Stripe 1%)</span>
                        <span data-testid="instant-fee">-{money(instant.fee_cents)}</span>
                      </div>
                      <div className="flex justify-between text-[#B7BABE]">
                        <span>You receive</span>
                        <span data-testid="instant-net">{money(instant.net_available_cents)}</span>
                      </div>
                    </div>
                    {!confirmCashout ? (
                      <button onClick={() => setConfirmCashout(true)} data-testid="instant-cashout-btn"
                        className="inline-flex items-center gap-2 mt-4 border border-[#E8C46A] bg-[#E8C46A]/10 hover:bg-[#E8C46A]/20 text-[#E8C46A] font-mono uppercase text-[11px] tracking-wide px-4 py-2.5 transition-colors">
                        <Zap className="w-3.5 h-3.5" /> Cash out instantly
                      </button>
                    ) : (
                      <div className="mt-4 border border-[#E8C46A]/40 bg-[#E8C46A]/5 p-3" data-testid="instant-confirm">
                        <p className="text-[12.5px] font-body text-[#D8C48A]">
                          Send {money(instant.net_available_cents)} to your {instant.card?.brand || "debit"} ••••{" "}
                          {instant.card?.last4} now? A 1% Stripe fee ({money(instant.fee_cents)}) is deducted —
                          standard payouts remain free.
                        </p>
                        <div className="flex items-center gap-2 mt-3">
                          <button onClick={doInstantCashout} disabled={cashingOut} data-testid="instant-confirm-btn"
                            className="inline-flex items-center gap-2 border border-olive bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-mono uppercase text-[11px] tracking-wide px-4 py-2 transition-colors">
                            {cashingOut ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} Confirm cash-out
                          </button>
                          <button onClick={() => setConfirmCashout(false)} disabled={cashingOut} data-testid="instant-cancel-btn"
                            className="border border-[#2E2F31] hover:border-[#3E3F42] text-[#8B8D90] font-mono uppercase text-[11px] tracking-wide px-4 py-2 transition-colors">
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}


            {/* RECONNECT — the stored payout account is orphaned. One plain instruction + the
                Reconnect button above mints a fresh account. Pending payouts hold until then. */}
            {reconnect && (
              <div className="mt-5 border border-[#A33D28]/50 bg-[#A33D28]/10 p-4" data-testid="payouts-reconnect-notice">
                <div className="flex items-center gap-2 text-[#E0A08F] font-mono uppercase text-[11px] tracking-wide">
                  <AlertTriangle className="w-4 h-4" /> {status?.reconnect_message || "Payouts need to be reconnected."}
                </div>
                <p className="text-[12.5px] font-body text-[#C9A79C] mt-2 max-w-[56ch]">
                  Your previous payout account can no longer be reached. Tap <span className="text-[#E0A08F] font-semibold">Reconnect payouts</span> to
                  set up a fresh Stripe account — any sales waiting on payout will release once you're reconnected.
                </p>
              </div>
            )}

            {/* UNKNOWN — we could not verify payout readiness LIVE with Stripe. Never claim
                "connected and payout-ready" off a stale flag; say so plainly. */}
            {unknown && !reconnect && (
              <div className="mt-5 border border-[#8A6D1E]/50 bg-[#8A6D1E]/10 p-4" data-testid="payouts-unknown-notice">
                <div className="flex items-center gap-2 text-[#E8C46A] font-mono uppercase text-[11px] tracking-wide">
                  <AlertTriangle className="w-4 h-4" /> Payout status unavailable
                </div>
                <p className="text-[12.5px] font-body text-[#D8C48A] mt-2 max-w-[56ch]">
                  We couldn't confirm your payout status with Stripe just now, so we won't show it as
                  ready. This is usually temporary — check back in a minute or hit refresh. No sale is
                  paid out until we can verify your account is live.
                </p>
              </div>
            )}

            {/* RESTRICTED / disabled reason — the Radar / capability-pause surface for the seller. */}
            {(restricted || disabledReason) && (
              <div className="mt-5 border border-[#A33D28]/50 bg-[#A33D28]/10 p-4" data-testid="payouts-restricted-notice">
                <div className="flex items-center gap-2 text-[#E0A08F] font-mono uppercase text-[11px] tracking-wide">
                  <AlertTriangle className="w-4 h-4" /> Action required by Stripe
                </div>
                <p className="text-[12.5px] font-body text-[#C9A79C] mt-2">
                  {DISABLED_COPY[disabledReason] || "Stripe needs more information before payouts can be enabled."}
                </p>
              </div>
            )}

            {/* Outstanding Stripe requirements. */}
            {reqs.length > 0 && (
              <div className="mt-5" data-testid="payouts-requirements">
                <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#76797D] mb-2">
                  Requirements still due
                </div>
                <ul className="space-y-1">
                  {reqs.map((r) => (
                    <li key={r} className="text-[12.5px] font-body text-[#B7BABE] flex items-center gap-2">
                      <span className="w-1 h-1 bg-[#E8C46A] rounded-full" /> {REQ_LABEL(r)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Sales already sold but held pending payout setup. */}
            {awaiting > 0 && !enabled && (
              <div className="mt-5 border border-[#E8C46A]/40 bg-[#E8C46A]/5 p-4" data-testid="payouts-awaiting">
                <p className="text-[12.5px] font-body text-[#E8C46A] flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {awaiting} sale{awaiting !== 1 ? "s" : ""} worth {money(awaitingAmt)} {awaiting !== 1 ? "are" : "is"} waiting
                  on payout setup. Finish setup to release {awaiting !== 1 ? "them" : "it"}.
                </p>
              </div>
            )}

            {pendingActivation && (
              <p className="text-[11px] font-mono uppercase text-secondary-foreground mt-4 flex items-center gap-1.5" data-testid="payouts-activation-pending">
                <Clock className="w-3.5 h-3.5" /> Marketplace payouts are being finalized — check back soon.
              </p>
            )}

            <Crosshair className="my-5" />
            <p className="text-[11.5px] font-body text-[#76797D]">
              Powered by Stripe Connect. OpticSwap never sees your bank details — they go straight to Stripe.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
